import { ContentBlock } from '@tytapp/content';
import { ContentBlockType } from '@tytapp/content';
import { ContentBlockEditorComponent } from '@tytapp/content/content-block-editor-component';
import { Component, HostBinding, inject } from '@angular/core';
import { ContentBlockViewComponent } from '@tytapp/content/content-block-view-component';
import { iframeResizer } from 'iframe-resizer';
import { ApiPodcast, ApiProduction, ApiShow, ApiVOD, ProductionsApi, ShowsApi } from '@tytapp/api';
import { Shell } from '@tytapp/common';
import { VideoPickerDialogComponent } from './video-picker/video-picker.component';

export interface VideoBlock extends ContentBlock {
    productionID: string;
    vodID: string;
    showComments?: boolean;
    showDescription?: boolean;
    showPlaylist?: boolean;
    showSocialButtons?: boolean;
}

@Component({
    template: `
    <ng-container *ngIf="block.productionID === 'preview'">
        <div class="preview">
            <img src="/assets/tap/video-preview.png" class="preview-image" />
            <mat-icon>play_circle</mat-icon>
        </div>
    </ng-container>
    <ng-container *ngIf="block.productionID !== 'preview'">
        <tyt-media-view
            *ngIf="getProduction()"
            [inEditor]="true"
            [production]="production"
            [show]="showForProduction(production)"
            [item]="vod"
            [mode]="playlistID"
            [showComments]="block.showComments"
            [showDescription]="block.showDescription"
            [showPlaylist]="block.showPlaylist"
            [showSocialButtons]="block.showSocialButtons"
            [embed]="true"
            [user]="user">
        </tyt-media-view>
    </ng-container>
    `,
    styles: [`
        :host {
            // This is needed in order for the <tyt-media-view> to handle smaller sizes
            // which are a subset of the page size, such as when the block is on a
            // sidebar
            container-name: media-view-container;
            container-type: inline-size;
            display: block;
        }

        .preview {
            aspect-ratio: 16/9;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;

            .preview-image {
                position: absolute;
                inset: 0;
                object-fit: cover;
                width: 100%;
                height: 100%;
                aspect-ratio: 16/9;
            }

            mat-icon {
                z-index: 2;
                $size: 48px;
                width: $size;
                height: $size;
                font-size: $size;
            }
        }
    `]
})
export class ViewComponent extends ContentBlockViewComponent<VideoBlock> {
    private showsApi = inject(ShowsApi);
    private productionsApi = inject(ProductionsApi);
    private shows: ApiShow[] = [];

    async ngOnInit() {
        this.shows = await this.showsApi.all().toPromise();
    }

    get vod() {
        if (!this.production)
            return undefined;

        let vods = [...this.production.full_length_vods, ...this.production.vod_clips];
        return vods.find(x => x.id === this.block.vodID);
    }

    get playlistID() {
        if (!this.production)
            return undefined;
        if (this.production.full_length_vods.some(x => x.id === this.block.vodID))
            return 'episodes';
        if (this.production.vod_clips.some(x => x.id === this.block.vodID))
            return 'clips';
    }

    private production: ApiProduction;
    private fetching = false;

    getProduction() {
        if (!this.fetching && this.block.productionID !== this.production?.id) {
            this.production = undefined;
            this.fetching = true;
            this.productionsApi.get(this.block.productionID).toPromise().then(production => {
                this.production = production;
                this.fetching = false;
            }).catch(e => {
                this.fetching = false;
            });
        }

        return this.production;
    }

    showForProduction(production: ApiProduction): ApiShow {
        return this.shows.find(x => x.id === production.show.id) ?? production.show;
    }
}

@Component({
    template: `
        <div class="property">
            <label>Production</label>
            <a url="/watch/edit/{{ block.productionID }}" target="_blank">{{ getProduction()?.internal_name ?? block.productionID }}</a>
        </div>
        <div class="property">
            <label>VOD</label>
            <a url="/watch/edit/{{ block.productionID }}/{{ playlistForItem(getProduction(), vod) }}" target="_blank">{{ vod?.internal_name ?? block.vodID }}</a>
        </div>

        <button mat-raised-button color="primary" (click)="chooseVideo()" [disabled]="locked">
            <mat-icon>search</mat-icon>
            Choose Video
        </button>

        <br/>
        <br/>

        <div class="toggle-options">
            <mat-checkbox [disabled]="locked" [(ngModel)]="block.showDescription">Show description</mat-checkbox>
            <mat-checkbox [disabled]="locked" [(ngModel)]="block.showPlaylist">Show playlist</mat-checkbox>
            <mat-checkbox [disabled]="locked" [(ngModel)]="block.showComments">Show comments</mat-checkbox>
            <mat-checkbox [disabled]="locked" [(ngModel)]="block.showSocialButtons">Show social buttons</mat-checkbox>
        </div>
    `,
    styles: [
        `
        :host {
            display: block;
        }

        .toggle-options {
            display: flex;
            flex-direction: column;
        }
        `
    ]
})
export class EditorComponent extends ContentBlockEditorComponent<VideoBlock> {
    private showsApi = inject(ShowsApi);
    private productionsApi = inject(ProductionsApi);
    private shell = inject(Shell);

    private shows: ApiShow[] = [];

    async ngOnInit() {
        this.shows = await this.showsApi.all().toPromise();
    }


    playlistForItem(production: ApiProduction, item: ApiVOD | ApiPodcast) {
        if (!production)
            return undefined;
        if (production.full_length_vods?.some(x => x.id === item.id))
            return 'episodes';
        if (production.vod_clips?.some(x => x.id === item.id))
            return 'clips';
        if (production.full_length_podcasts?.some(x => x.id === item.id))
            return 'podcasts';
        if (production.audio_clips?.some(x => x.id === item.id))
            return 'audio-stories';
    }

    chooseVideo() {
        this.shell.showDialog(VideoPickerDialogComponent, (production, item) => {
            this.block.productionID = production.id;
            this.block.vodID = item.id;
        });
    }

    private production: ApiProduction;

    get vod() {
        if (!this.production)
            return undefined;

        let vods = [...this.production.full_length_vods ?? [], ...this.production.vod_clips ?? []];
        return vods.find(x => x.id === this.block.vodID);
    }

    fetching = false;

    private fetchedProductions = new Map<string, Promise<ApiProduction>>();

    getProduction() {
        if (!this.fetchedProductions.has(this.block.productionID)) {
            this.fetching = true;
            this.fetchedProductions.set(
                this.block.productionID,
                this.productionsApi.get(this.block.productionID).toPromise().then(production => {
                    this.production = production;
                    this.fetching = false;
                    return production;
                }).catch(e => {
                    this.fetching = false;
                    return undefined;
                })
            );
        }

        return this.production;
    }

    showForProduction(production: ApiProduction): ApiShow {
        return this.shows.find(x => x.id === production.show.id) ?? production.show;
    }

}


export const VIDEO_BLOCK_TYPE: ContentBlockType<VideoBlock> = {
    id: 'video',
    label: 'Video (TYT.com)',
    icon: 'play_arrow',
    template: {
        productionID: '',
        vodID: ''
    },
    preview: {
        productionID: 'preview',
        vodID: 'preview'
    },
    previewSettings: {
        verticalAlign: 'center'
    },
    editorComponent: EditorComponent,
    viewComponent: ViewComponent
}